import { authFlowDebug } from '../../../debug/authFlowDebug';

const WAX_NODES_LIST = 'https://validate.eosnation.io/wax/reports/endpoints.json';

const FALLBACK_ENDPOINTS = [
  'https://wax.api.atomicassets.io',
  'https://wax-aa.eosdac.io',
  'https://wax.eosusa.io',
  'https://atomic.hivebp.io',
  'https://wax-aa.eu.eosamsterdam.net',
  'https://wax-atomic-api.eosphere.io',
  'https://atomic.wax.eosrio.io',
  'https://wax-atomic.alcor.exchange',
  'https://atomic-api.wax.cryptolions.io',
  'https://wax-atomic.eosiomadrid.io',
  'https://atomic.3dkrender.com',
  'https://aa.dapplica.io',
]

const normalizeAtomicEndpoint = (node) => {
  if (typeof node === 'string') return node;
  if (Array.isArray(node) && typeof node[1] === 'string') return node[1];
  if (node && typeof node.url === 'string') return node.url;
  return null;
};

const normalizeAtomicEndpoints = (nodes = []) => {
  const urls = nodes
    .map(normalizeAtomicEndpoint)
    .filter((node) => /^https?:\/\//.test(node));

  return [...new Set(urls)];
};

/** Load community-maintained validator feed */
const getAtomicEndpoints = async () => {
  try {
    const res = await fetch(WAX_NODES_LIST, { cache: 'no-store' });
    const raw = await res.json();
    const endpoints = normalizeAtomicEndpoints(raw?.report?.atomic_https);
    if (!endpoints.length) {
      throw new Error('No AtomicAssets endpoints returned');
    }
    console.debug('[AA] Loaded endpoints from community feed:', endpoints);
    authFlowDebug('atomicApi.endpoints.loaded', {
      count: endpoints.length,
      firstEndpoint: endpoints[0],
    });
    return endpoints;
  } catch (err) {
    console.error('Failed to fetch wax atomic api list. Using fallback list.', err);
    authFlowDebug('atomicApi.endpoints.fallback', {
      errMessage: err && err.message,
      count: FALLBACK_ENDPOINTS.length,
      firstEndpoint: FALLBACK_ENDPOINTS[0],
    });
    return FALLBACK_ENDPOINTS;
  }
};

const fetchFromAny = async (
  path,
  abortMs = 10_000,
) => {
  const endpoints = await getAtomicEndpoints();
  const failures = [];

  for (const node of endpoints) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), abortMs);
    const startedAt = Date.now();
    try {
      authFlowDebug('atomicApi.fetch.attempt', {
        node,
        path,
        abortMs,
      });
      const res = await fetch(`${node}${path}`, {
        headers: { accept: 'application/json' },
        signal: controller.signal,
        mode: 'cors',
        credentials: 'omit',
      });
      clearTimeout(timer);
      if (res.ok) {
        const body = await res.json();
        authFlowDebug('atomicApi.fetch.success', {
          node,
          status: res.status,
          ms: Date.now() - startedAt,
          dataCount: body && body.data && body.data.length,
        });
        return body;
      }
      console.warn(`[AtomicAssets] ${node} → ${res.status}`);
      authFlowDebug('atomicApi.fetch.httpError', {
        node,
        status: res.status,
        ms: Date.now() - startedAt,
      });
      failures.push(`${node}: ${res.status}`);
    } catch (err) {
      console.warn(`[AtomicAssets] ${node} failed`, err);
      authFlowDebug('atomicApi.fetch.error', {
        node,
        errName: err && err.name,
        errMessage: err && err.message,
        ms: Date.now() - startedAt,
      });
      failures.push(`${node}: ${err && err.message}`);
    } finally {
      clearTimeout(timer);
    }
  }
  authFlowDebug('atomicApi.fetch.allFailed', {
    path,
    failures,
  });
  throw new Error(`All AtomicAssets endpoints failed: ${failures.join('; ')}`);
};

export {
  fetchFromAny,
  normalizeAtomicEndpoints,
};
